import React, { useState, useMemo, useRef } from 'react'
import './edit-staff.scss'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage } from '@/utils/layer'
import { Column, Expaned, Row } from '@/components/layout/layout'
import SuperAdmin from '@/assets/images/super-admin.svg'
import { StaffList } from '../staff-list-table/staff-list-table'
import { useRequest, useSelections } from 'ahooks'
import { $post } from '@/api'
import Iconfont from '@/components/iconfont/iconfont'
import { getBEMCls } from '@/utils/classname'
import Button from '@/components/button/button'
import FormTitle from '../../../goods-manage/components/form/form-title/form-title'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import { pcPerssion, lsPerssion, cashierPerssion } from '@/config/role-config'
import { Switch, Spin, Popover } from 'antd'
import Input from '@/components/input/input'
import FormItem from '../../../goods-manage/components/form/form-item/form-item'
import $msg from '@/utils/message'
import { IUser } from '@/model/types/user'
import { staffListModel } from '../model'

interface EditStaffProps {
    staffId: number
    isNewStaff?: boolean
    refresh?: () => void
}
/**
 * 如需增加（删除）权限 更新 ‘@/config/role-config’ 配置文件即可
 */
const EditStaff: React.FC<EditStaffProps> = props => {
    const [staffInfo, setStaffInfo] = useState<StaffList | null>(null)
    const [userInfo, setUserInfo] = useState<IUser | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [remark, setRemak] = useState<string>('')
    const [staffList, setStaffList] = useState<StaffList[]>([])
    const [popoverVisable, setPopoverVisable] = useState<boolean>(false)
    // 开关
    const [pcCheck, setPcCheck] = useState<boolean>(true)
    const [lsCheck, setLsCheck] = useState<boolean>(true)
    const [cashierCheck, setCashierCheck] = useState<boolean>(true)
    // 权限集合
    const pcPerssions = useRef<number[]>(Object.keys(pcPerssion).map(i => +i))
    const lsPerssions = useRef<number[]>(Object.keys(lsPerssion()).map(i => +i))
    const cashierPerssions = useRef<number[]>(Object.keys(cashierPerssion).map(i => +i))
    //  pc后台权限
    const pcPerssionAction = useSelections<number>(pcPerssions.current)
    // 零售通权限
    const lsPerssionAction = useSelections<number>(lsPerssions.current)
    // 收银台权限
    const cashierPerssionAction = useSelections<number>(cashierPerssions.current)

    const setAllData = useSetDataTogether()

    const cls = (...arg: string[]) => getBEMCls('edit-staff', ...arg)

    useRequest<IUser>(
        () =>
            $post({
                url: '/staff/getstaffapplicationrecordinfo',
                params: {
                    staff_application_record_id: props.staffId,
                },
            }),
        {
            manual: !props.isNewStaff,
            onSuccess: res => {
                setAllData(() => {
                    setUserInfo(res)
                    setLoading(false)
                })
            },
        },
    )

    useRequest<StaffList>(
        () =>
            $post({
                url: '/staff/getstaffinfo',
                params: {
                    staff_id: props.staffId,
                },
            }),
        {
            manual: !!props.isNewStaff,
            onSuccess: res => {
                setAllData(() => {
                    changePerssion(res.permission_list)
                    setStaffInfo(res)
                    setUserInfo(res.user)
                    setRemak(res.remarks || '')
                    setLoading(false)
                })
            },
        },
    )

    const changePerssion = (record: number[]) => {
        const includesPerssion = (persions: number[]) => record.filter(p => persions.includes(p))
        setAllData(() => {
            pcPerssionAction.setSelected(includesPerssion(pcPerssions.current))
            lsPerssionAction.setSelected(includesPerssion(lsPerssions.current))
            cashierPerssionAction.setSelected(includesPerssion(cashierPerssions.current))
            setPcCheck(record.includes(1))
            setCashierCheck(record.includes(2))
            setLsCheck(record.includes(17))
        })
    }

    useRequest<{
        staff_list: StaffList[]
    }>(
        () =>
            $post({
                url: '/staff/getstafflist',
                params: {
                    limit: 20,
                    page: 1,
                    status: 0,
                },
            }),
        {
            onSuccess: res => {
                setStaffList(res.staff_list)
            },
        },
    )

    const passStaffReq = useRequest(
        () =>
            $post({
                url: '/staff/staffapplicationpass',
                params: {
                    staff_application_record_id: props.staffId,
                    remarks: remark,
                    permission_list: [
                        ...pcPerssionAction.selected,
                        ...lsPerssionAction.selected,
                        ...cashierPerssionAction.selected,
                    ],
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '新增成功')
                layerManage.pop()
                staffListModel.setUpdateApplyNum()
                props.refresh && props.refresh()
            },
            onError: e => $msg('error', e.message),
        },
    )

    const editStaffReq = useRequest(
        () =>
            $post({
                url: '/staff/editstaffinfo',
                params: {
                    permission_source_staff_id: staffInfo?.permission_source_staff.id,
                    staff_id: props.staffId,
                    remarks: remark,
                    permission_list: [
                        ...pcPerssionAction.selected,
                        ...lsPerssionAction.selected,
                        ...cashierPerssionAction.selected,
                    ],
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '修改成功')
                layerManage.pop()
            },
            onError: e => $msg('error', e.message),
        },
    )

    const isAdmin = useMemo(() => staffInfo?.creator_name === staffInfo?.user.realname && !props.isNewStaff, [
        props.isNewStaff,
        staffInfo,
    ])

    const adminMsg = () => $msg('error', '超管权限不可编辑')

    const editPurchasePerssion = () => {
        if (lsPerssionAction.isSelected(31)) {
            lsPerssionAction.setSelected(lsPerssionAction.selected.filter(i => ![39, 40, 31].includes(i)))
        } else {
            lsPerssionAction.setSelected([...lsPerssionAction.selected, 39, 40, 31])
        }
    }

    return (
        <BaseDrawer
            width={564}
            title={props.isNewStaff ? '员工申请通过' : '编辑员工'}
            isShow
            onClose={() => layerManage.pop()}
        >
            <Column data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={loading}>
                        <Column>
                            <Row data-padding-left-20 data-padding-top-16>
                                <img className={cls('avatar')} src={userInfo?.avatar} alt="" />
                                <Column>
                                    <Row>
                                        <div className={cls('nickname')}>{userInfo?.nickname}</div>
                                        {isAdmin && <img data-margin-left-4 alt="" src={SuperAdmin} />}
                                    </Row>
                                    <div data-font-size-14>真实姓名：{userInfo?.realname}</div>
                                    <Row>
                                        <Row data-align-items-center>
                                            <Iconfont name="icon5shengri" size={16} />
                                            <div className={cls('birthday')}>{userInfo?.birthday || '-'}</div>
                                        </Row>
                                        <Row data-align-items-center data-margin-left-8>
                                            <Iconfont name="icon5shoujihao" size={16} />
                                            <div className={cls('birthday')}>{userInfo?.phone}</div>
                                        </Row>
                                    </Row>
                                </Column>
                            </Row>
                            <FormItem title="备注">
                                <Input
                                    name="name"
                                    reg={/^.{0,30}$/}
                                    value={remark}
                                    onChange={val => {
                                        setRemak(val.target.value)
                                    }}
                                    placeholder="输入备注"
                                    inputsuffix={() => (
                                        <span data-font-size-14 data-color-000>
                                            {remark.length}/30
                                        </span>
                                    )}
                                />
                            </FormItem>
                            <div className={cls('line')} />
                            <FormTitle hasLine={false} title="权限设置">
                                <Popover
                                    visible={popoverVisable}
                                    onVisibleChange={() => setPopoverVisable(!popoverVisable)}
                                    trigger="click"
                                    placement="bottomLeft"
                                    content={
                                        <Column className={cls('staff-container')}>
                                            {staffList.map(s => (
                                                <Row
                                                    className={cls('staff-item')}
                                                    key={s.id}
                                                    onClick={() => {
                                                        if (isAdmin) return adminMsg()
                                                        setPopoverVisable(false)
                                                        changePerssion(s.permission_list)
                                                    }}
                                                >
                                                    <img alt="" src={s.user.avatar} className="avatar" />
                                                    <div className="nickname">{s.user.nickname}</div>
                                                    <div className="realname">({s.user.realname})</div>
                                                </Row>
                                            ))}
                                        </Column>
                                    }
                                >
                                    <div className="action" data-margin-right-20>
                                        复制他人权限
                                    </div>
                                </Popover>
                            </FormTitle>
                            <Column data-margin-bottom-32 className={cls('perssion')}>
                                <Row data-align-items-center data-justify-content-space-between data-margin-bottom-12>
                                    <Row data-align-items-center>
                                        <div data-font-size-14 data-margin-right-10>
                                            进入零售后台
                                        </div>
                                        <Switch
                                            checked={pcCheck}
                                            onChange={() => {
                                                if (isAdmin) return adminMsg()
                                                setAllData(() => {
                                                    pcPerssionAction.toggle(1)
                                                    setPcCheck(!pcCheck)
                                                })
                                            }}
                                        />
                                    </Row>
                                    <Row
                                        hidden={!pcCheck}
                                        data-align-items-center
                                        onClick={() => {
                                            if (isAdmin) return adminMsg()
                                            pcPerssionAction.toggleAll()
                                        }}
                                    >
                                        <Iconfont
                                            name={
                                                pcPerssionAction.allSelected
                                                    ? 'icon3duoxuanerji-20'
                                                    : 'icon3duoxuanguan-20'
                                            }
                                            size={20}
                                        />
                                        <div data-font-size-12 data-margin-left-6>
                                            全选
                                        </div>
                                    </Row>
                                </Row>
                                <div className={cls('perssion-container')}>
                                    {Object.keys(pcPerssion).map(p => (
                                        <div
                                            className={cls(
                                                'item',
                                                pcCheck && pcPerssionAction.isSelected(+p) ? 'active' : 'unactive',
                                            )}
                                            key={p}
                                            onClick={() => {
                                                if (isAdmin) return adminMsg()
                                                if (pcCheck) {
                                                    pcPerssionAction.toggle(+p)
                                                }
                                            }}
                                        >
                                            <div
                                                className="check"
                                                hidden={!(pcCheck && pcPerssionAction.isSelected(+p))}
                                            >
                                                <Iconfont name="icongouxuan" size={14} />
                                            </div>
                                            {pcPerssion[p]}
                                        </div>
                                    ))}
                                </div>
                            </Column>
                            <Column data-margin-bottom-32 className={cls('perssion')}>
                                <Row data-align-items-center data-justify-content-space-between data-margin-bottom-12>
                                    <Row data-align-items-center>
                                        <div data-font-size-14 data-margin-right-10>
                                            进入收银台
                                        </div>
                                        <Switch
                                            checked={cashierCheck}
                                            onChange={() => {
                                                if (isAdmin) return adminMsg()
                                                setAllData(() => {
                                                    pcPerssionAction.toggle(2)
                                                    setCashierCheck(!cashierCheck)
                                                })
                                            }}
                                        />
                                    </Row>
                                    <Row
                                        data-align-items-center
                                        hidden={!cashierCheck}
                                        onClick={() => {
                                            if (isAdmin) return adminMsg()
                                            cashierPerssionAction.toggleAll()
                                        }}
                                    >
                                        <Iconfont
                                            name={
                                                cashierPerssionAction.allSelected
                                                    ? 'icon3duoxuanerji-20'
                                                    : 'icon3duoxuanguan-20'
                                            }
                                            size={20}
                                        />
                                        <div data-font-size-12 data-margin-left-6>
                                            全选
                                        </div>
                                    </Row>
                                </Row>
                                <div className={cls('perssion-container')}>
                                    {Object.keys(cashierPerssion).map(p => (
                                        <div
                                            className={cls(
                                                'item',
                                                cashierCheck && cashierPerssionAction.isSelected(+p)
                                                    ? 'active'
                                                    : 'unactive',
                                            )}
                                            key={p}
                                            onClick={() => {
                                                if (isAdmin) return adminMsg()
                                                cashierCheck && cashierPerssionAction.toggle(+p)
                                            }}
                                        >
                                            <div
                                                className="check"
                                                hidden={!(cashierCheck && cashierPerssionAction.isSelected(+p))}
                                            >
                                                <Iconfont name="icongouxuan" size={14} />
                                            </div>
                                            {cashierPerssion[p]}
                                        </div>
                                    ))}
                                </div>
                            </Column>
                            <Column data-margin-bottom-32 className={cls('perssion')}>
                                <Row data-align-items-center data-justify-content-space-between data-margin-bottom-12>
                                    <Row data-align-items-center>
                                        <div data-font-size-14 data-margin-right-10>
                                            进入零售通
                                        </div>
                                        <Switch
                                            checked={lsCheck}
                                            onChange={() => {
                                                if (isAdmin) return adminMsg()
                                                setAllData(() => {
                                                    pcPerssionAction.toggle(17)
                                                    setLsCheck(!lsCheck)
                                                })
                                            }}
                                        />
                                    </Row>
                                    <Row
                                        data-align-items-center
                                        hidden={!lsCheck}
                                        onClick={() => {
                                            if (isAdmin) return adminMsg()
                                            lsPerssionAction.toggleAll()
                                        }}
                                    >
                                        <Iconfont
                                            name={
                                                lsPerssionAction.allSelected
                                                    ? 'icon3duoxuanerji-20'
                                                    : 'icon3duoxuanguan-20'
                                            }
                                            size={20}
                                        />
                                        <div data-font-size-12 data-margin-left-6>
                                            全选
                                        </div>
                                    </Row>
                                </Row>
                                <div className={cls('perssion-container')}>
                                    {Object.keys(lsPerssion()).map(p => (
                                        <div
                                            className={cls(
                                                'item',
                                                lsCheck && lsPerssionAction.isSelected(+p) ? 'active' : 'unactive',
                                            )}
                                            key={p}
                                            onClick={() => {
                                                if (isAdmin) return adminMsg()
                                                if (+p === 31) {
                                                    return editPurchasePerssion()
                                                }
                                                lsCheck && lsPerssionAction.toggle(+p)
                                            }}
                                        >
                                            <div
                                                className="check"
                                                hidden={!(lsCheck && lsPerssionAction.isSelected(+p))}
                                            >
                                                <Iconfont name="icongouxuan" size={14} />
                                            </div>
                                            {lsPerssion()[p]}
                                        </div>
                                    ))}
                                </div>
                            </Column>
                        </Column>
                    </Spin>
                </Expaned>
                <div className={cls('bottom')}>
                    <Button
                        loading={editStaffReq.loading || passStaffReq.loading || void 0}
                        types="default"
                        onClick={() => {
                            if (props.isNewStaff) {
                                passStaffReq.run()
                            } else {
                                editStaffReq.run()
                            }
                        }}
                    >
                        确认{props.isNewStaff ? '通过' : '修改'}
                    </Button>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default EditStaff
